/**
 * Scheduler: connects the audio pipeline to the inference worker.
 *
 * Strategy: "latest chunk wins"
 *   - Audio pipeline produces 5 s chunks continuously.
 *   - If the Worker is idle, immediately send the chunk for inference.
 *   - If the Worker is busy, overwrite `pendingChunk` (old data discarded).
 *   - When inference completes, check for a pending chunk and send it.
 */
import type { LlamaWasmWorker } from "@zhoukezi/llama-wasm";
import { AudioPipeline } from "./audio-pipeline.ts";
import {
  setPhase,
  setCurrentText,
  setMetrics,
  setChunkCount,
  setAudioLevel,
  setChunkProgress,
  prompt,
  chunkCount,
} from "./store.ts";

export class Scheduler {
  private client: LlamaWasmWorker;
  private pipeline: AudioPipeline;
  private pendingChunk: Float32Array | null = null;
  private inferring = false;
  private stopped = false;
  private animFrameId = 0;
  private chunkIndex = 0;

  constructor(client: LlamaWasmWorker, pipeline: AudioPipeline) {
    this.client = client;
    this.pipeline = pipeline;

    this.pipeline.onChunk = (pcm) => this.handleChunk(pcm);
    this.pipeline.onSilence = () => this.handleSilence();
    this.pipeline.onProgress = (p) => setChunkProgress(p);
  }

  /** Start the recording -> inference loop and visualisation updates. */
  start(): void {
    this.stopped = false;
    setPhase("recording");
    this.startAudioLevelLoop();
  }

  /** Stop everything. */
  stop(): void {
    this.stopped = true;
    this.pendingChunk = null;
    cancelAnimationFrame(this.animFrameId);
    this.pipeline.dispose();
  }

  // ── Chunk handling ──────────────────────────────────────────────────────

  private handleChunk(pcm: Float32Array): void {
    if (this.stopped) return;
    if (this.inferring) {
      // Worker busy — park this chunk (overwrites previous pending)
      this.pendingChunk = pcm;
      return;
    }
    void this.runInference(pcm);
  }

  private handleSilence(): void {
    if (this.stopped) return;
    setPhase("silence");
    // Briefly flash silence state, then back to recording
    setTimeout(() => {
      if (!this.stopped) setPhase("recording");
    }, 400);
  }

  /**
   * Rough token count estimator.
   * LLM tokenizers typically produce ~1.3 tokens per whitespace-separated word
   * for English text. This is a display-only approximation.
   */
  private static estimateTokens(text: string): number {
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words * 1.3));
  }

  private async runInference(pcm: Float32Array): Promise<void> {
    if (this.stopped) return;
    this.inferring = true;
    setPhase("inferring");
    setChunkProgress(0); // reset progress ring while inferring

    const t0 = performance.now();
    const idx = this.chunkIndex++;

    try {
      const text = await this.client.inferAudio(pcm, prompt(), 64);

      if (this.stopped) return;

      const elapsed = (performance.now() - t0) / 1000;
      const tokens = Scheduler.estimateTokens(text);
      const tokPerSec = elapsed > 0 ? tokens / elapsed : 0;
      setCurrentText(text);
      setMetrics({ text, elapsed, tokens, tokPerSec, chunkIndex: idx });
      setChunkCount(chunkCount() + 1);
    } catch (err) {
      console.error("[inference]", err);
    } finally {
      this.inferring = false;
    }

    // Check for pending chunk — skip phase flicker by going straight
    // into the next inference without setting "recording" in between.
    if (!this.stopped && this.pendingChunk) {
      const next = this.pendingChunk;
      this.pendingChunk = null;
      void this.runInference(next);
    } else if (!this.stopped) {
      setPhase("recording");
    }
  }

  // ── Audio level for visualisation ───────────────────────────────────────

  private startAudioLevelLoop(): void {
    const tick = () => {
      if (this.stopped) return;
      this.animFrameId = requestAnimationFrame(tick);
      const analyser = this.pipeline.analyserNode;
      const freqData = this.pipeline.freqData;
      if (!analyser || !freqData) return;
      analyser.getByteFrequencyData(freqData);
      let sum = 0;
      for (let i = 0; i < freqData.length; i++) sum += freqData[i]!;
      setAudioLevel(sum / freqData.length / 255);
    };
    this.animFrameId = requestAnimationFrame(tick);
  }
}
